// Redux
import { AsyncResult, Action, ApiAction, Result } from '..';
import api from '../redux-api';
import { ReduxState } from './types';

// Types
import { QuestionnaireState } from './types/questionnaire';

// Utils
import { deepClone } from '../../utils/objects';
import { QUESTIONNAIRE_TYPES } from '../../utils/questionnaire';
import Questionnaire from '@/types/questionnaire';
import QuestionnaireSectionItem from '@/types/questionnaire-section-item';
import QuestionItem from '@/types/question-item';
import QuestionOptionItem from '@/types/question-option-item';
import QuestionnaireAnswers from '@/types/questionnaire-answers';
import SavedSections from '@/types/saved-sections';
import QUESTION_TYPES from '@/types/question-types';

const INITIAL_STATE: QuestionnaireState = {
  questionnaires: [],
  isCompleted: false,
  savedSections: {},
};

// Actions
const GET_QUESTIONNAIRES: ApiAction = {
  type: 'connect/questionnaire/get-many',
  endpoint: 'questionnaires/',
};

const LOAD_QUESTIONNAIRE: Action = {
  type: 'connect/questionnaire/load-questionnaire',
};

const COMPLETE_QUESTIONNAIRE: Action = {
  type: 'connect/questionnaire/complete-questionnaire',
};

const GO_BACK_QUESTIONNAIRE: Action = {
  type: 'connect/questionnaire/go-back-questionnaire',
};

const GET_QUESTIONNAIRE_ANSWERS_BY_ID: ApiAction = {
  type: 'connect/questionnaire/get-questionnaire-answer',
  endpoint: 'questionnaire-answers/:id/',
};

const SAVE_QUESTIONNAIRE_ANSWERS: ApiAction = {
  type: 'connect/questionnaire/save-answer',
  endpoint: 'questionnaire-answers/',
};

const SET_SAVED_SECTIONS: Action = {
  type: 'connect/questionnaire/set-saved-sections-questionnaire',
};

const RESET_QUESTIONNAIRE: Action = {
  type: 'connect/questionnaire/reset-questionnaire',
};

const UPDATE_QUESTIONNAIRE_ANSWERS: ApiAction = {
  type: 'connect/questionnaire/update-answers',
  endpoint: 'questionnaire-answers/:id/',
};

const DELETE_QUESTIONNAIRE_ANSWERS: ApiAction = {
  type: 'connect/questionnaire/delete-answer',
  endpoint: 'questionnaire-answers/:id/',
};

// Reducer
export default function reducer(state: QuestionnaireState = INITIAL_STATE, action: Action): QuestionnaireState {
  switch (action.type) {
    case `${GET_QUESTIONNAIRES.type}_SUCCESS`: {
      return {
        ...state,
        questionnaires: action.data.data,
      };
    }
    case LOAD_QUESTIONNAIRE.type: {
      return {
        ...state,
        currentQuestionnaire: action.questionnaire,
      };
    }
    case COMPLETE_QUESTIONNAIRE.type: {
      return {
        ...state,
        isCompleted: true,
      };
    }
    case GO_BACK_QUESTIONNAIRE.type: {
      return {
        ...state,
        isCompleted: false,
      };
    }
    case `${GET_QUESTIONNAIRE_ANSWERS_BY_ID.type}_SUCCESS`:
    case `${UPDATE_QUESTIONNAIRE_ANSWERS.type}_SUCCESS`:
    case `${SAVE_QUESTIONNAIRE_ANSWERS.type}_SUCCESS`: {
      return {
        ...state,
        isCompleted: !!action.data.data,
        answers: action.data.data,
      };
    }
    case `${DELETE_QUESTIONNAIRE_ANSWERS.type}_SUCCESS`: {
      return {
        ...state,
        isCompleted: false,
        answers: action.data.data,
        savedSections: {},
      };
    }
    case SET_SAVED_SECTIONS.type: {
      return {
        ...state,
        savedSections: action.savedSections,
      };
    }
    case RESET_QUESTIONNAIRE.type: {
      return {
        ...state,
        isCompleted: false,
        savedSections: {},
        answers: undefined,
      };
    }
    default: {
      return state;
    }
  }
}

const getStateForType = (type: QUESTIONNAIRE_TYPES, state: ReduxState) => {
  switch (type) {
    case QUESTIONNAIRE_TYPES.RATIONALE:
    default:
      return state.rationaleQuestionnaire;
  }
};

export const getQuestionnaires =
  (reducerName: QUESTIONNAIRE_TYPES): AsyncResult<Questionnaire[]> =>
  () =>
    api.get({ ...GET_QUESTIONNAIRES, name: reducerName }, { type: reducerName }).then((res: { data: any }) => res.data as Questionnaire[]);

export const loadQuestionnaire =
  (reducerName: QUESTIONNAIRE_TYPES, questionnaire: Questionnaire): Result<void> =>
  dispatch => {
    dispatch({ ...LOAD_QUESTIONNAIRE, name: reducerName, questionnaire });
  };

export const getUnansweredQuestions =
  (reducerName: QUESTIONNAIRE_TYPES, section: QuestionnaireSectionItem): Result<QuestionItem[]> =>
  (dispatch, getState) => {
    const { savedSections } = getStateForType(reducerName, getState());
    const sectionId = section._id;

    return section.questions.filter(question => {
      if (question.optional) {
        return false;
      }

      return !savedSections || !(sectionId in savedSections) || !(question._id in savedSections[sectionId].questions);
    });
  };

export const saveAnswers =
  (
    reducerName: QUESTIONNAIRE_TYPES,
    sectionId: string,
    questionId: string,
    option: QuestionOptionItem,
    text: string | undefined,
    notes: string | undefined,
  ): Result<void> =>
  (dispatch, getState) => {
    const { savedSections, questionnaires } = getStateForType(reducerName, getState());

    if (questionnaires == null || questionnaires[0] == null) {
      return;
    }

    const questionnaire = questionnaires[0];
    let clonedSections = deepClone(savedSections);

    const currentSection = questionnaire.sections.find(section => section._id === sectionId)!;
    const currentQuestion = currentSection.questions.find(question => question._id === questionId)!;
    const isMultiSelect = currentQuestion.type === QUESTION_TYPES.MULTIPLE_CHOICES_MULTI_SELECT;

    if (!clonedSections) {
      clonedSections = {};
    }

    if (!(sectionId in clonedSections)) {
      clonedSections[sectionId] = {
        questions: {},
      };
    }

    let newOptions = [option._id];
    if (isMultiSelect) {
      const currentOptions: string[] | undefined = clonedSections[sectionId].questions[questionId]?.options;
      newOptions = currentOptions ? [...currentOptions] : [];
      const index = newOptions.indexOf(option._id);
      if (index === -1) {
        newOptions.push(option._id);
      } else if (newOptions.length > 1) {
        newOptions.splice(index, 1);
      }
    }

    const optionData = {
      // no multiple answers for none-kyc questionnaire. Simply add the id to an array
      options: newOptions,
      text,
      value: 0,
      notes,
    };

    clonedSections[sectionId].questions[questionId] = optionData;

    dispatch({ type: SET_SAVED_SECTIONS.type, name: reducerName, savedSections: clonedSections });
  };

export const clearAnswers =
  (reducerName: QUESTIONNAIRE_TYPES, sectionId: string, questionId: string): Result<void> =>
  (dispatch, getState) => {
    const { savedSections } = getStateForType(reducerName, getState());
    const clonedSections = deepClone(savedSections);

    if (!clonedSections || !(sectionId in clonedSections)) {
      return;
    }

    if (questionId in clonedSections[sectionId].questions) {
      delete clonedSections[sectionId].questions[questionId];
    }

    dispatch({ type: SET_SAVED_SECTIONS.type, name: reducerName, savedSections: clonedSections });
  };

export const saveNotes =
  (reducerName: QUESTIONNAIRE_TYPES, sectionId: string, questionId: string, notes: string): Result<void> =>
  (dispatch, getState) => {
    const { savedSections, questionnaires } = getStateForType(reducerName, getState());

    if (questionnaires == null || questionnaires[0] == null) {
      return;
    }

    let clonedSections = deepClone(savedSections);

    if (!clonedSections) {
      clonedSections = {};
    }

    if (!(sectionId in clonedSections)) {
      clonedSections[sectionId] = {
        questions: {},
      };
    }

    const questionData = clonedSections[sectionId].questions[questionId]
      ? {
          ...clonedSections[sectionId].questions[questionId],
          notes,
        }
      : {
          options: [],
          notes,
          value: 0,
        };

    clonedSections[sectionId].questions[questionId] = questionData;

    dispatch({ type: SET_SAVED_SECTIONS.type, name: reducerName, savedSections: clonedSections });
  };

export const clearNotes =
  (reducerName: QUESTIONNAIRE_TYPES, sectionId: string, questionId: string): Result<void> =>
  (dispatch, getState) => {
    const { savedSections } = getStateForType(reducerName, getState());
    const clonedSections = deepClone(savedSections);

    if (!clonedSections || !(sectionId in clonedSections)) {
      return;
    }

    if (questionId in clonedSections[sectionId].questions) {
      delete clonedSections[sectionId].questions[questionId].notes;
    }

    dispatch({ type: SET_SAVED_SECTIONS.type, name: reducerName, savedSections: clonedSections });
  };

export const getSectionSubtotal =
  (reducerName: QUESTIONNAIRE_TYPES, section: QuestionnaireSectionItem): Result<number> =>
  (dispatch, getState) => {
    const { savedSections } = getStateForType(reducerName, getState());
    const sectionId = section._id;

    return section.questions.reduce((sum, question) => {
      if (!savedSections || !savedSections[sectionId] || !savedSections[sectionId].questions[question._id]) {
        return sum;
      }

      return sum + savedSections[sectionId].questions[question._id].value;
    }, 0);
  };

export const completeQuestionnaire =
  (reducerName: QUESTIONNAIRE_TYPES): Result<void> =>
  dispatch => {
    dispatch({ ...COMPLETE_QUESTIONNAIRE, name: reducerName });
  };

export const goBackQuestionnaire =
  (reducerName: QUESTIONNAIRE_TYPES): Result<void> =>
  dispatch => {
    dispatch({ ...GO_BACK_QUESTIONNAIRE, name: reducerName });
  };

export const getQuestionnaireAnswersById =
  (reducerName: QUESTIONNAIRE_TYPES, id?: string): AsyncResult<QuestionnaireAnswers> =>
  () =>
    api
      .get({ ...GET_QUESTIONNAIRE_ANSWERS_BY_ID, name: reducerName }, { id })
      .then((res: { data: any }) => res.data as QuestionnaireAnswers)
      .catch(() => ({}) as QuestionnaireAnswers);

export const saveQuestionnaireAnswers =
  (
    reducerName: QUESTIONNAIRE_TYPES,
    questionnaireAnswers: QuestionnaireAnswers,
    questionnaire: Questionnaire,
    comparison: string | null,
  ): AsyncResult<QuestionnaireAnswers> =>
  () => {
    const data = {
      answers: questionnaireAnswers.answers,
      questionnaire: questionnaire._id,
      comparison,
    };

    return api.post({ ...SAVE_QUESTIONNAIRE_ANSWERS, name: reducerName }, null, data).then((res: { data: any }) => res.data as QuestionnaireAnswers);
  };

export const updateQuestionnaireAnswers =
  (reducerName: QUESTIONNAIRE_TYPES, id: string, questionnaireAnswers: QuestionnaireAnswers, questionnaire: Questionnaire): AsyncResult<QuestionnaireAnswers> =>
  () => {
    const data = {
      answers: questionnaireAnswers.answers,
      questionnaire: questionnaire._id,
    };

    return api.put({ ...UPDATE_QUESTIONNAIRE_ANSWERS, name: reducerName }, { id }, data).then((res: { data: any }) => res.data as QuestionnaireAnswers);
  };

export const deleteQuestionnaireAnswers =
  (reducerName: QUESTIONNAIRE_TYPES, id: string): AsyncResult<QuestionnaireAnswers> =>
  () =>
    api.delete({ ...DELETE_QUESTIONNAIRE_ANSWERS, name: reducerName }, { id }).then(() => ({}) as QuestionnaireAnswers);

export const questionnaireIsCompleted =
  (reducerName: QUESTIONNAIRE_TYPES): Result<boolean> =>
  (dispatch, getState) =>
    getStateForType(reducerName, getState()).isCompleted;

export const getSavedSections =
  (reducerName: QUESTIONNAIRE_TYPES): Result<SavedSections> =>
  (dispatch, getState) =>
    getStateForType(reducerName, getState()).savedSections;

export const setSavedSections =
  (reducerName: QUESTIONNAIRE_TYPES, savedSections: SavedSections): Result<void> =>
  dispatch => {
    dispatch({ ...SET_SAVED_SECTIONS, name: reducerName, savedSections });
  };

export const resetQuestionnaire =
  (reducerName: QUESTIONNAIRE_TYPES): Result<void> =>
  dispatch => {
    dispatch({ ...RESET_QUESTIONNAIRE, name: reducerName });
  };
